import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlusCircle, ArrowRight, AlertTriangle, Users, Inbox } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending:            "bg-amber-100 text-amber-900 border-amber-200",
  pending_department: "bg-purple-100 text-purple-900 border-purple-200",
  pending_cmo:        "bg-indigo-100 text-indigo-900 border-indigo-200",
  needs_info: "bg-blue-100 text-blue-900 border-blue-200",
  approved:   "bg-emerald-100 text-emerald-900 border-emerald-200",
  booked:     "bg-[#E9F5DB] text-[#1B4332] border-[#1B4332]/20",
  declined:   "bg-red-100 text-red-900 border-red-200",
};
const STATUS_LABELS = {
  pending: "Pending review",
  pending_department: "Awaiting dept head",
  pending_cmo: "Awaiting CMO",
  needs_info: "Needs info",
  approved: "Approved",
  booked: "Booked",
  declined: "Declined",
};

export default function GuestRequests() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("guest_requests.manage");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(canManage ? "pending" : "all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/guest-requests");
      setItems(data || []);
    } catch {
      toast.error("Failed to load guest requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (tab === "all") return items;
    if (tab === "active") return items.filter((r) => ["pending", "pending_department", "pending_cmo", "needs_info", "approved"].includes(r.status));
    if (tab === "in_approval") return items.filter((r) => ["pending_department", "pending_cmo"].includes(r.status));
    return items.filter((r) => r.status === tab);
  }, [items, tab]);

  const counts = useMemo(() => ({
    pending: items.filter((r) => r.status === "pending").length,
    in_approval: items.filter((r) => ["pending_department", "pending_cmo"].includes(r.status)).length,
    needs_info: items.filter((r) => r.status === "needs_info").length,
    approved: items.filter((r) => r.status === "approved").length,
    booked: items.filter((r) => r.status === "booked").length,
    declined: items.filter((r) => r.status === "declined").length,
  }), [items]);

  return (
    <div className="space-y-6 fade-in" data-testid="guest-requests-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            Guest requests
          </h1>
          <p className="text-[#525C58] mt-1">
            {canManage
              ? "Non-medical guest bookings awaiting your review. Approve, decline, or ask for more info."
              : "Submit booking requests for non-medical visitors (family, friends, donors)."}
          </p>
        </div>
        <Button onClick={() => navigate("/guest-requests/new")} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="new-guest-request-btn">
          <PlusCircle className="h-4 w-4 mr-2" /> New guest request
        </Button>
      </div>

      {canManage && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="gr-tabs">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending {counts.pending > 0 && <Badge variant="secondary" className="ml-2">{counts.pending}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="in_approval" data-testid="tab-in-approval">
              In approval {counts.in_approval > 0 && <Badge variant="secondary" className="ml-2">{counts.in_approval}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="needs_info" data-testid="tab-needs-info">
              Needs info {counts.needs_info > 0 && <Badge variant="secondary" className="ml-2">{counts.needs_info}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">
              Approved {counts.approved > 0 && <Badge variant="secondary" className="ml-2">{counts.approved}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="booked" data-testid="tab-booked">Booked</TabsTrigger>
            <TabsTrigger value="declined" data-testid="tab-declined">Declined</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {loading ? (
        <div className="py-20 text-center text-[#7A8A84]">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card className="border-[#E8E8E6]">
          <CardContent className="py-16 text-center text-[#7A8A84]">
            <Inbox className="h-8 w-8 mx-auto mb-2 opacity-40" />
            {tab === "pending" ? "No pending requests — nice work!" : "No guest requests yet."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <Card
              key={r.id}
              className={`border-[#E8E8E6] hover:border-[#1B4332]/40 transition cursor-pointer ${r.conflict_check?.has_conflict && r.status === "pending" ? "ring-1 ring-amber-300" : ""}`}
              onClick={() => navigate(`/guest-requests/${r.id}`)}
              data-testid={`gr-row-${r.id}`}
            >
              <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Users className="h-4 w-4 text-[#1B4332]" />
                    <h3 className="font-semibold text-[#1C1F1E]">{r.guest_name}</h3>
                    <Badge variant="outline" className={STATUS_STYLES[r.status] || ""}>
                      {STATUS_LABELS[r.status] || r.status}
                    </Badge>
                    {r.hospital_work && (
                      <Badge variant="outline" className="bg-[#F4F0FF] text-purple-800 border-purple-200 text-xs">
                        Hospital work{r.work_department ? ` · ${r.work_department}` : ""}
                      </Badge>
                    )}
                    {r.conflict_check?.has_conflict && r.status !== "declined" && r.status !== "booked" && (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium">
                        <AlertTriangle className="h-3.5 w-3.5" /> clash
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#525C58] mt-1">
                    {r.arrival_date} → {r.departure_date} · {r.num_guests} guest{r.num_guests === 1 ? "" : "s"}
                    {r.relationship && <> · {r.relationship}</>}
                  </p>
                  <p className="text-xs text-[#7A8A84] mt-0.5 truncate">
                    Submitted by {r.submitted_by_name} ({r.submitted_by_role}) · {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#7A8A84]" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
